'use client'

import { useEffect, useRef, useState } from 'react'

import { useViewport } from '@/components/findex-connect/hooks/useViewport'
import { Section, useThemeStore } from '@/components/findex-connect/store/ThemeStore'
import { ServicesBlockSb } from '@/components/findex-connect/types/component-types-sb'

import { ServiceCard } from '../ServiceCard/ServiceCard'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselNext,
  CarouselPrevious,
} from '../ui/carousel'

type ServicesBlockProps = {
  sectionId: Section
  data: ServicesBlockSb
}

export const ServicesBlock: React.FC<ServicesBlockProps> = ({ sectionId, data }) => {
  const [api, setApi] = useState<CarouselApi>()
  const [activeItem, setActiveItem] = useState(0)
  const { viewportWidth } = useViewport()
  const isMobile = viewportWidth < 768
  const sectionRef = useRef(null)
  // Helpers
  const serviceBackgroundColors = data?.serviceCards?.map(({ color }) => color || '#d8e6f4')

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: isMobile ? '0% 0% -30% 0%' : '10% 0% -50% 0%',
      threshold: isMobile ? 0.4 : 0.8,
    }

    const observer = (() => {
      if (!isMobile) {
        return new IntersectionObserver((entries) => {
          if (!isMobile) {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                useThemeStore.setState({
                  activeSection: sectionId,
                  backgroundColor: serviceBackgroundColors?.[activeItem] || '#d8e6f4',
                })
              } else {
                useThemeStore.setState({ backgroundColor: 'white' })
              }
            })
          }
        }, observerOptions)
      }
    })()

    if (sectionRef?.current && !isMobile) {
      observer?.observe(sectionRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionRef, sectionId, useThemeStore, activeItem, isMobile])

  useEffect(() => {
    if (!api) {
      return
    }

    api.on('select', () => {
      setActiveItem(api.selectedScrollSnap())
    })
  }, [api])

  // Data
  const { serviceCards, heading } = data
  if (!data || !serviceCards || !heading) return null

  return (
    <div
      className="SERVICES-BLOCK-CONTAINER mx-auto w-full overflow-hidden transition-colors duration-700 ease-in-out"
      id={sectionId}
      // Mobile just paints the backgound colour on the whole container
      style={
        isMobile
          ? {
              backgroundColor: serviceBackgroundColors?.[activeItem] || '#d8e6f4',
            }
          : {}
      }
    >
      <div className="SERVICE-BLOCK-INNER mx-auto flex w-full flex-col overflow-visible py-10 md:py-14">
        <div className="SERVICESBLOCK-HEADER max-w-container p-gutter mx-auto mb-4 flex w-full flex-col md:mb-16">
          {heading && (
            <h2
              className="mb-6 self-start p-1 md:mb-12"
              tabIndex={0}
              ref={!isMobile ? sectionRef : null}
            >
              {heading}
            </h2>
          )}
          <div className="SERVICE-NAV-CONTAINER hidden flex-wrap gap-3 md:flex md:max-w-[86%]">
            {serviceCards?.map(({ title }, itemNumber) => (
              <button
                className={
                  'SERVICE-NAV-ITEM tab-outline-primary border-subtle-light text-subtle-light cursor-pointer rounded-full border bg-none px-4 py-2 ' +
                  (activeItem === itemNumber ? ' text-text border-text' : ' ')
                }
                key={`selector-${itemNumber}-${title}`}
                onClick={() => api?.scrollTo(itemNumber)}
              >
                {title || 'Other Service'}
              </button>
            ))}
          </div>
        </div>
        <div className="CAROUSEL-CONTAINER max-w-container relative mx-auto w-full overflow-hidden md:overflow-visible">
          <Carousel className="CAROUSEL" opts={{ align: 'start' }} setApi={setApi}>
            <CarouselPrevious className="border-subtle text-subtle top-[96%] left-6 z-10 size-10 cursor-pointer bg-white/0 hover:scale-105 hover:bg-white/0 md:-top-21 md:right-23 md:left-[unset]" />
            <CarouselNext className="border-subtle text-subtle top-[96%] left-19 z-10 size-10 cursor-pointer bg-white/0 hover:scale-105 hover:bg-white/0 md:-top-21 md:right-10 md:left-[unset]" />
            <CarouselContent className="CAROUSEL-CONTENT ml-6 gap-6 pb-26 md:ml-12 md:gap-10 md:overflow-visible md:pb-2">
              {serviceCards?.map((item, index, allItems) => {
                return (
                  <ServiceCard
                    title={item.title || ''}
                    subtitle={item.subtitle || ''}
                    contentItems={item.contentItems || []}
                    image={item.image}
                    key={`${index}-${item.title}`}
                    isLastItem={index === allItems.length - 1}
                    isActiveCard={activeItem === index}
                  />
                )
              })}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </div>
  )
}
